import { Injectable } from '@angular/core';
import { PaginationHelper } from '../_helpers/paginationHelper';
import { UnitStandardType } from '../config/enums';
import { AccessLevels } from '../config/enums/accessLevel';
import { ChatRoomTypes } from '../config/enums/chatRoomTypes';
import { StudentTypes } from '../config/enums/studentTypes';
import { QueryStringParameters } from '../shared/classes/query-string-parameters';
import { UrlBuilder } from '../shared/classes/url-builder';
import { AnnouncementMessageTypes } from './../config/enums/announcementMessageTypes';
import { LmsApi } from './_api/lms-api';
import { EnvConfigurationService } from './_env-config.service';

@Injectable({ providedIn: 'root' })
export class ApiEndpointsService {
  constructor(private configService: EnvConfigurationService) {}

  public info() {
    return this.createUrl('info', true);
  }

  public facilityGroup(id: number = null) {
    if (this.isNull(id)) {
      return this.createUrl('FacilityGroup');
    }
    return this.createUrl(`FacilityGroup/${id}`);
  }

  public learnershipDocuments(learnershipId: number, id: number = null) {
    if (this.isNull(id)) {
      return this.createUrl(`Learnership/${learnershipId}/documents`);
    }
    return this.createUrl(`Learnership/${learnershipId}/documents/${id}`);
  }

  public learnershipDocumentsByLearnership(learnershipId: number) {
    return this.createUrl(
      `Learnership/documents/byLearnership/${learnershipId}`
    );
  }

  public archiveLearnershipDocument(docId: number) {
    return this.createUrl(`Learnership/documents/archive/${docId}`);
  }

  public archiveStudentDocument(docId: number) {
    return this.createUrl(`Student/documents/archive/${docId}`);
  }

  public getLearnershipGeneralDocumentInfo(docId: number) {
    return this.createUrl(`Learnership/documents/doc-info/${docId}`);
  }

  public downloadLearnershipDocument(docId: number) {
    return this.createUrl(`Learnership/documents/download/${docId}`);
  }

  public qualDocuments(qualificaitonId: number, id: number = null) {
    if (this.isNull(id)) {
      return this.createUrl(`Qualification/${qualificaitonId}/documents`);
    }
    return this.createUrl(`Qualification/${qualificaitonId}/documents/${id}`);
  }

  public qualDocumentsByQualification(qualificationId: number) {
    return this.createUrl(
      `Qualification/documents/byQualification/${qualificationId}`
    );
  }

  public archiveQualDocument(id: number) {
    return this.createUrl(`Qualification/documents/archive/${id}`);
  }

  public downloadQualDocument(id: number) {
    return this.createUrl(`Qualification/documents/download/${id}`);
  }

  public getQualDocumentInfo(id: number) {
    return this.createUrl(`Qualification/documents/doc-info/${id}`);
  }

  public qualLearningMaterial(qualId: number) {
    return this.createUrl(`Qualification/${qualId}/learning-material`);
  }

  public qualLearningMaterialByQualification(
    qualificationId: number,
    qualPOEGroupId: number = null,
    staffOnly: boolean = null
  ) {
    return this.createUrlWithQueryParameters(
      `Qualification/learning-material/byQualification/${qualificationId}`,

      (queryParams) => {
        if (!this.isNull(qualPOEGroupId)) {
          queryParams.push('qualPoeGroupId', qualPOEGroupId);
        }

        if (!this.isNull(staffOnly)) {
          queryParams.push('staffOnly', staffOnly);
        }
      }
    );
  }

  public picture(id: string = null) {
    if (this.isNull(id)) {
      return this.createUrl('picture');
    }
    return this.createUrl(`picture/${id}`);
  }

  public poeGroupsForCurrentUser() {
    return this.createUrl('Qualification/poe-groups/current-user');
  }

  public poeGroupByQualification(qualificationId: number) {
    return this.createUrl(
      `Qualification/poe-groups/by-qualification/${qualificationId}`
    );
  }

  public qualLearningMaterialByStudent(qualPOEGroupId: number) {
    return this.createUrlWithQueryParameters(
      'Qualification/learning-material/forCurrentUser',

      (queryParams) => {
        queryParams.push('qualPoeGroupId', qualPOEGroupId);
      }
    );
  }

  public getStudentGeneralDocumentInfo(docId: number) {
    return this.createUrl(`Student/document/doc-info/${docId}`);
  }

  public currentUserGeneralStudentDocs(): string {
    return this.createUrl(
      'Student/documents/general-student-documents/current-user'
    );
  }

  public downloadGeneralStudentDocuments(docId: number) {
    return this.createUrl(`Student/documents/download/${docId}`);
  }

  public archiveQualLearningMaterial(id: number) {
    return this.createUrl(`Qualification/learning-material/archive/${id}`);
  }

  public downloadQualLearningMaterial(id: number) {
    return this.createUrl(`Qualification/learning-material/download/${id}`);
  }

  public uploadStudentCodeFile(studentLearnershipId: number) {
    return this.createUrl(`StudentCode/upload/file/${studentLearnershipId}`);
  }

  public organizationalDocuments(id: number = null) {
    if (this.isNull(id)) {
      return this.createUrl('Organization/documents');
    }
    return this.createUrl(`Organization/documents/${id}`);
  }

  public archiveOrganizationalDocuments(id: number) {
    return this.createUrl(`Organization/documents/archive/${id}`);
  }

  public downloadOrganizationalDocument(id: number) {
    return this.createUrl(`Organization/documents/download/${id}`);
  }

  public dashboardInfo() {
    return this.createUrl('Dashboard/tileInfo');
  }

  public accreditedProviders(
    qualificationType: LmsApi.QualificationType = null
  ) {
    return this.createUrlWithQueryParameters(
      `AccreditedProvider`,
      (queryString) => {
        if (qualificationType !== null) {
          queryString.push('qualificationType', qualificationType);
        }
      }
    );
  }

  public getAccreditedProvider(id: number) {
    return this.createUrl(`AccreditedProvider/${id}`);
  }

  public archiveAccreditedProvider(id: number) {
    return this.createUrl(`AccreditedProvider/archive/${id}`);
  }

  public hubConnection() {
    return this.createUrl('rtHubSocket', true);
  }

  public sendAnnouncement() {
    return this.createUrl('ChatHub/send-announcement');
  }

  public sendLearnershipGroupMessage() {
    return this.createUrl('ChatHub/send-learnership-group-message');
  }

  public sendInternshipGroupMessage() {
    return this.createUrl('ChatHub/send-internship-group-messaget');
  }

  public getAnnouncements(
    announcementType: AnnouncementMessageTypes,
    paginationHelper: PaginationHelper
  ) {
    return this.createUrlWithQueryParameters(
      'ChatHub/announcements',
      (queryString) => {
        queryString.push('announcementType', announcementType);
        queryString.push('pageNumber', paginationHelper.pageNumber);
        queryString.push('pageSize', paginationHelper.pageSize);
        queryString.push(
          'newAnnouncementsClientSide',
          paginationHelper.newRecordsClientSide
        );
      }
    );
  }

  public getChatRoomMessages(
    chatRoomType: ChatRoomTypes,
    chatRoomGroupName: string,
    paginationHelper: PaginationHelper
  ) {
    return this.createUrlWithQueryParameters(
      'ChatHub/chatRoomMessages',
      (queryString) => {
        queryString.push('chatRoomType', chatRoomType);
        queryString.push('chatRoomGroupName', chatRoomGroupName);
        queryString.push('pageNumber', paginationHelper.pageNumber);
        queryString.push('pageSize', paginationHelper.pageSize);
        queryString.push(
          'newMessagesClientSide',
          paginationHelper.newRecordsClientSide
        );
      }
    );
  }

  public getAttachment(attchmentId: number) {
    return this.createUrl(
      `ChatHub/announcement-attachment/download/${attchmentId}`
    );
  }

  public getAllAttachments(announcementId: number) {
    return this.createUrl(
      `ChatHub/announcement-attachment/download-all/${announcementId}`
    );
  }

  // TENANT
  public tenantBySubDomain(subDomainName: string) {
    return this.createUrl(`Tenant/subDomain/${subDomainName}`);
  }

  public authenticateTenantUser() {
    return this.createUrl('Tenant/authenticateTenantUser');
  }

  // PROGRAMMES------------------------------------------

  public programme(id?: number): string {
    if (!id) {
      return this.createUrl('Programme');
    } else {
      return this.createUrl(`Programme/${id}`);
    }
  }

  public programmesIdNameType() {
    return this.createUrl('Programme/IdNameType');
  }

  public programmeIdName(id: number) {
    return this.createUrl(`Programme/IdName/${id}`);
  }

  public programmesTopLevel() {
    return this.createUrl('Programme/TopLevel');
  }

  public learnershipsTopLevel() {
    return this.createUrl('Programme/TopLevelLearnerships');
  }

  public learnership(
    id: number | null = null,
    active: boolean | null = null
  ): string {
    if (this.isNull(id)) {
      return this.createUrlWithQueryParameters('Learnership', (queryParams) => {
        if (active !== null) {
          queryParams.push('active', active);
        }
      });
    } else {
      return this.createUrl(`Learnership/${id}`);
    }
  }

  public addCandidatesToLearnership() {
    return this.createUrl('Learnership/add-candidates');
  }

  public endDateLearnership() {
    return this.createUrl('Learnership/end-date');
  }

  public removeStudentFromLearnership() {
    return this.createUrl('Learnership/remove-student');
  }

  public removeStaffMemberFromLearnership() {
    return this.createUrl('Learnership/remove-staff-member');
  }

  public assignStaffMembersToLearnership() {
    return this.createUrl('Learnership/assign-staff');
  }

  public reInstateStudentIntoLearnership() {
    return this.createUrl('Learnership/re-instate-student');
  }

  public learnershipFacilitators(learnershipId: number) {
    return this.createUrl(
      `Learnership/facilitators/by-learnership/${learnershipId}`
    );
  }

  public learnershipGroups(learnershipId: number) {
    return this.createUrl(`Learnership/groups/by-learnership/${learnershipId}`);
  }

  public internship(id?: number): string {
    if (!id) {
      return this.createUrl('Internship');
    } else {
      return this.createUrl(`Internship/${id}`);
    }
  }

  public learnershipProgrammes() {
    return this.createUrl('Programme/Learnerships');
  }

  public internshipsTopLevel() {
    return this.createUrl('Programme/TopLevelInternships');
  }

  public internshipProgrammes() {
    return this.createUrl('Programme/Internships');
  }

  // QUALIFICATIONS------------------------------------------

  public qualification(id?: number): string {
    if (!id) {
      return this.createUrl('Qualification');
    } else {
      return this.createUrl(`Qualification/${id}`);
    }
  }

  public archiveQualification(id: number): string {
    return this.createUrl(`Qualification/archive/${id}`);
  }

  public unitStandardsIdName(qualificationId: number) {
    return this.createUrl(
      `Qualification/UnitStandardsIdName/${qualificationId}`
    );
  }

  public unitStandards(qualificationId: number, type?: UnitStandardType) {
    if (type === undefined) {
      return this.createUrl(`Qualification/unitStandards/${qualificationId}`);
    }
    return this.createUrlWithQueryParameters(
      `Qualification/unitStandards/${qualificationId}`,
      (queryParams) => {
        queryParams.push('type', type);
      }
    );
  }

  public unitStandardsByPOEGroup(poeType: LmsApi.POETypes, poeGroupId: number) {
    return this.createUrlWithQueryParameters(
      'Qualification/poeGroupUnitstandards',
      (queryParams) => {
        queryParams.push('poeType', poeType);
        queryParams.push('poeGroupId', poeGroupId);
      }
    );
  }

  public unitStandardsIdNameByProgramme(id: number) {
    return this.createUrl(`Qualification/UnitStandardsIdNameByProgramme/${id}`);
  }

  public unitStandardsIdNameByStudent(id: number) {
    return this.createUrl(`Qualification/UnitStandardsIdNameByStudent/${id}`);
  }

  public qualificationTopLevel(): string {
    return this.createUrl('Qualification/TopLevel');
  }

  public qualificationWithUnitStandards(id: number): string {
    return this.createUrl(`Qualification/WithUnitStandards/${id}`);
  }

  public qualificationsWithUnitStandards(): string {
    return this.createUrl('Qualification/withUnitStandards');
  }

  // INTERNSHIP-PLANS------------------------------------------

  public internshipPlan(id?: number): string {
    if (!id) {
      return this.createUrl('InternshipPlan');
    } else {
      return this.createUrl(`InternshipPlan/${id}`);
    }
  }

  public internshipPlansTopLevel() {
    return this.createUrl('InternshipPlan/TopLevel');
  }

  // STUDENTS------------------------------------------
  public studentProgrammeInfo(studentId: number) {
    return this.createUrl(`Student/programmeInfo/${studentId}`);
  }

  public student(id?: number): string {
    if (!id) {
      return this.createUrl('Student');
    } else {
      return this.createUrl(`Student/${id}`);
    }
  }

  public registerCandidateAsStudent(id: number): string {
    return this.createUrl(`Student/register/candidateAsStudent/${id}`);
  }

  public registerCandidate(): string {
    return this.createUrl('Student/register/candidate');
  }

  public resendVerificationEmail(): string {
    return this.createUrl('Tenant/resendVerficationEmail');
  }

  public studentWithDetails(id: number, activeOnly = true) {
    if (activeOnly) {
      return this.createUrl(`Student/WithDetails/Active/${id}`);
    }
    return this.createUrl(`Student/WithDetails/${id}`);
  }

  public studentWithAddress(id: number) {
    return this.createUrl(`Student/WithAddressDetails/${id}`);
  }

  public studentIdName(id: number): string {
    return this.createUrl(`Student/IdName/${id}`);
  }

  public studentIdNamePoeIdProgrammeId(id: number): string {
    return this.createUrl(`Student/IdNamePoeIdProgrammeId/${id}`);
  }

  public studentByUserId(id: number): string {
    return this.createUrl(`Student/userId/${id}`);
  }
  public studentsByProgramme(id: number): string {
    return this.createUrl(`Student/ByProgramme/${id}`);
  }
  public candidates(): string {
    return this.createUrl('Student/Candidates');
  }
  public candidatesIdName(): string {
    return this.createUrl('Student/CandidatesIdName');
  }
  public internsIdName(): string {
    return this.createUrl('Student/InternsIdName');
  }
  public learnersIdName(): string {
    return this.createUrl('Student/LearnersIdName');
  }
  public learners(): string {
    return this.createUrl('Student/learners');
  }
  public interns(): string {
    return this.createUrl('Student/interns');
  }

  public getStudentsByType(
    type: StudentTypes,
    active: boolean | null = null
  ): string {
    return this.createUrlWithQueryParameters(
      'Student/byType',
      (queryParams) => {
        queryParams.push('type', type);
        if (active === true) {
          queryParams.push('active', active);
        }
      }
    );
  }

  public studentsBulkUpload(
    studentType: StudentTypes,
    programmeId: number
  ): string {
    return this.createUrl(`Student/Bulk/${studentType}/${programmeId}`);
  }

  public downloadBulkStudentTemplate() {
    return this.createUrl('Student/downloadBulkTemplate');
  }

  public studentPdfDetailsByUserId(id: number) {
    return this.createUrl(`Student/PdfDetails/ByUserId/${id}`);
  }

  // student_personal_files

  public studentPersonalFiles(id: number = null): string {
    if (this.isNull(id)) {
      return this.createUrl(`Student/personalFiles`);
    } else {
      return this.createUrl(`Student/personalFiles/${id}`);
    }
  }
  public uploadCandidatePersonalFiles(
    tenantId: number,
    candidateId: number
  ): string {
    return this.createUrl(
      `Student/register/${tenantId}/candidate/${candidateId}/personal-files`
    );
  }

  public uploadStudentPersonalFiles(studentId: number): string {
    return this.createUrl(`Student/${studentId}/personalFiles`);
  }

  public removeStudentDisabilityDoc(id: number) {
    return this.createUrl(`StudentPersonalFile/RemoveDisabilityDoc/${id}`);
  }

  public replaceStudentFile(fileId: number, studentId: number) {
    return this.createUrl(`StudentPersonalFile/${fileId}/${studentId}`);
  }

  public downloadStudentPersonalFile(id: number) {
    return this.createUrl(`Student/personalFiles/download/${id}`);
  }

  public deleteFileByStudentID(id: number) {
    return this.createUrl(`StudentPersonalFile/student/${id}`);
  }

  public archiveStudentPersonalFile(id: number) {
    return this.createUrl(`Student/personalFiles/archive/${id}`);
  }

  // Staff

  public staffTypesByUserId(userId: number) {
    return this.createUrl(`Staff/types/userId/${userId}`);
  }

  public staff(id?: number): string {
    if (!id) {
      return this.createUrl('Staff');
    } else {
      return this.createUrl(`Staff/${id}`);
    }
  }

  public staffWithAddress(id: number) {
    return this.createUrl(`Staff/WithAddressDetails/${id}`);
  }

  public staffIdName(id?: number): string {
    if (!id) {
      return this.createUrl('Staff/IdName');
    } else {
      return this.createUrl(`Staff/IdName/${id}`);
    }
  }

  public activeStaff() {
    return this.createUrl('Staff/Active');
  }

  public sortedStaff(onlyActive = true) {
    if (onlyActive) {
      return this.createUrl('Staff/Sorted/Active');
    } else {
      return this.createUrl('Staff/Sorted');
    }
  }

  public staffTopLevel(id) {
    return this.createUrl(`Staff/TopLevel/${id}`);
  }

  public staffByUserId(id): string {
    return this.createUrl(`Staff/userId/${id}`);
  }

  public staffByProgramme(id): string {
    return this.createUrl(`Staff/ByProgramme/${id}`);
  }

  public staffPdfDetailsByUserId(id: number) {
    return this.createUrl(`Staff/pdfViewerDetails/userId/${id}`);
  }

  public signatureSvg(accountId: number = null) {
    if (this.isNull(accountId)) {
      return this.createUrl(`Account/signature/${accountId}`);
    }
    return this.createUrl('Account/signature');
  }

  public initialsSvg(accountId: number = null) {
    if (this.isNull(accountId)) {
      return this.createUrl(`Account/initials/${accountId}`);
    }
    return this.createUrl('Account/initials');
  }

  public userProfilePicture() {
    return this.createUrl(`Account/profile-picture`);
  }

  // student_personal_files

  public staffPersonalFiles(id: number = null): string {
    if (this.isNull(id)) {
      return this.createUrl(`Staff/personalFiles`);
    } else {
      return this.createUrl(`Staff/personalFiles/${id}`);
    }
  }

  public uploadStaffPersonalFiles(staffId: number): string {
    return this.createUrl(`Staff/${staffId}/personalFiles`);
  }

  public removeStaffDisabilityDoc(id: number) {
    return this.createUrl(`StaffPersonalFile/RemoveDisabilityDoc/${id}`);
  }

  public replaceStaffFile(fileId: number, staffId: number) {
    return this.createUrl(`StaffPersonalFile/${fileId}/${staffId}`);
  }

  public downloadStaffPersonalFile(id: number) {
    return this.createUrl(`Staff/personalFiles/download/${id}`);
  }

  public deleteFileByStaffID(id: number) {
    return this.createUrl(`StaffPersonalFile/staff/${id}`);
  }

  public archiveStaffPersonalFile(id: number) {
    return this.createUrl(`Staff/personalFiles/archive/${id}`);
  }

  // Attendance_Registers

  public attendanceRegisterFiles(id?: number): string {
    if (!id) {
      return this.createUrl('AttendanceRegister');
    } else {
      return this.createUrl(`AttendanceRegister/${id}`);
    }
  }

  public createAttendanceRegister() {
    return this.createUrl(`Learnership/attendance-register`);
  }

  public createLearnershipAttendanceRegisterFile(attendanceRegisterId: number) {
    return this.createUrl(
      `Learnership/create-learnership-attendance-register-pdf/${attendanceRegisterId}`
    );
  }

  public downloadlearnershipAttendanceRegistersByGroupId(
    attendanceRegisterId: number
  ) {
    return this.createUrl(
      `Learnership/attendance-register/by-learnership/download/${attendanceRegisterId}`
    );
  }

  public learnershipAttendanceRegistersByLearnershipId(
    learnershipId: number
  ): string {
    {
      return this.createUrl(
        `Learnership/attendance-registers/by-learnership/${learnershipId}`
      );
    }
  }

  public learnershipAttendanceRegisterExportInfoById(
    attendanceRegisterId: number
  ): string {
    {
      return this.createUrl(
        `Learnership/attendance-register-export-info/${attendanceRegisterId}`
      );
    }
  }

  public learnershipAttendanceRegisterItemsByAttendanceRegisterId(
    attendanceRegisterId: number
  ): string {
    {
      return this.createUrl(
        `Learnership/attendance-register-items/by-attendance-register/${attendanceRegisterId}`
      );
    }
  }

  public updateLearnershipAttendanceRegisterItemById(
    attendanceRegisterItemId: number
  ): string {
    return this.createUrl(
      `Learnership/attendance-register-item/${attendanceRegisterItemId}`
    );
  }

  public downloadAttendanceRegisterFiles(learnershipId: number) {
    return this.createUrl(`AttendanceRegister/download/${learnershipId}`);
  }

  public downloadAttendanceRegisterFileNew(attendanceRegisterId: number) {
    return this.createUrl(
      `Learnership/attendance-register-file/download/${attendanceRegisterId}`
    );
  }

  // User_Details

  public userDetails(id?: number): string {
    if (!id) {
      return this.createUrl('User');
    } else {
      return this.createUrl(`User/${id}`);
    }
  }

  public authenticateUser(): string {
    return this.createUrl('Tenant/authenticateTenantUser');
  }

  public revokeToken(): string {
    return this.createUrl('Account/revoke-token');
  }

  public refreshToken(): string {
    return this.createUrl('Account/refresh-token');
  }

  public forgotPassword(): string {
    return this.createUrl('Account/forgot-password');
  }

  public verifyEmail(): string {
    return this.createUrl('Account/verify-email');
  }

  public confirmRegistration(): string {
    return this.createUrl('Tenant/confirm-registration');
  }

  public resetPassword(): string {
    return this.createUrl('Tenant/reset-password');
  }

  public validateResetToken(): string {
    return this.createUrl('Account/validate-reset-token');
  }

  // custom User

  public customUser(id?: number): string {
    if (!id) {
      return this.createUrl('CustomUser');
    } else {
      return this.createUrl(`CustomUser/${id}`);
    }
  }

  public customUserByUserID(id: number): string {
    return this.createUrl(`CustomUser/ByUserID/${id}`);
  }

  public distinctCustomUserRoles(): string {
    return this.createUrl('CustomUser/DistinctRoles');
  }

  // POE
  public configurePoe(qualificationId: number): string {
    return this.createUrl(`Poe/QualPOE/Configure/${qualificationId}`);
  }

  public qualPOEGroups(): string {
    return this.createUrl('Poe/qualPOE/groups');
  }

  public poeByProgramme(id: number) {
    return this.createUrl(`Poe/ByProgramme/${id}`);
  }

  public downloadPoeDocumentsByLearnerId(poeId: number) {
    return this.createUrl(`POE/programmePOE/download-all/${poeId}`);
  }

  public programmePOEScheduleEvents(id: number = null) {
    if (this.isNull(id)) {
      return this.createUrl('POE/programmePOE/schedule/events');
    } else {
      return this.createUrl(`POE/programmePOE/schedule/events/${id}`);
    }
  }

  public programmePOESchedule(programmePoeId: number) {
    return this.createUrl(`POE/programmePOE/${programmePoeId}/schedule`);
  }

  public programmePOEScheduleExportedDocs(programmePoeId: number) {
    return this.createUrl(`POE/programmePOE/schedule/${programmePoeId}`);
  }

  public poeDocs(id: number, poeFolder: LmsApi.POEFolders, isChild = false) {
    switch (poeFolder) {
      case LmsApi.POEFolders.General:
        return this.poeGeneralDocs(id, isChild);
      case LmsApi.POEFolders.AssessmentGuide:
        return this.poeAssessmentGuideDocs(id, isChild);
      case LmsApi.POEFolders.Feedback:
        return this.poeFeedbackDocs(id, isChild);
      case LmsApi.POEFolders.Workplace:
        return this.poeWorkplaceDocs(id, isChild);
      case LmsApi.POEFolders.GroupFormativeAssessments:
        return this.poeGroupFormativeAssessmentDocs(id, isChild);
      case LmsApi.POEFolders.GroupSummativeAssessments:
        return this.poeGroupSummativeAssessmentDocs(id, isChild);
      case LmsApi.POEFolders.GroupSaqaDocs:
        return this.poeGroupSaqaDocs(id, isChild);
      case LmsApi.POEFolders.GroupGeneral:
        return this.poeGroupGeneralDocs(id, isChild);
      default:
        break;
    }
  }

  public qualPOEGeneralDocs(qualPOEId: number): string {
    return this.createUrl(`POE/qualPOE/documents/general/${qualPOEId}`);
  }

  public poeGeneralDocs(id: number, isChild = false) {
    if (isChild) {
      return this.createUrl(`Poe/Child/GeneralDocs/${id}`);
    }
    return this.createUrl(`Poe/GeneralDocs/${id}`);
  }

  public downloadPoeGeneralDoc(id: number, isChild = false) {
    if (isChild) {
      return this.createUrl(`Poe/Child/GeneralDocs/Download/${id}`);
    }
    return this.createUrl(`Poe/GeneralDocs/Download/${id}`);
  }

  public archivePoeGeneralDoc(id: number) {
    return this.createUrl(`Poe/GeneralDocs/Archive/${id}`);
  }

  public poeGeneralDocAnnotationData(id: number = null, isChild = false) {
    if (isChild) {
      if (id === null) {
        return this.createUrl(`Poe/Child/GeneralDocs/AnnotationData`);
      }
      return this.createUrl(`Poe/Child/GeneralDocs/AnnotationData/${id}`);
    }

    if (id === null) {
      return this.createUrl(`Poe/GeneralDocs/AnnotationData`);
    }
    return this.createUrl(`Poe/GeneralDocs/AnnotationData/${id}`);
  }

  public replacePoeGeneralDoc(docId: number, isChild = false) {
    if (isChild) {
      return this.createUrl(`Poe/Child/GeneralDocs/Replace/${docId}`);
    }
    return this.createUrl(`Poe/GeneralDocs/Replace/${docId}`);
  }

  public poeAssessmentGuideDocs(id: number, isChild = false) {
    if (isChild) {
      return this.createUrl(`Poe/Child/AssessmentGuideDocs/${id}`);
    }
    return this.createUrl(`Poe/AssessmentGuideDocs/${id}`);
  }

  public downloadPoeAssessmentGuideDocs(id: number, isChild = false) {
    if (isChild) {
      return this.createUrl(`Poe/Child/AssessmentGuideDocs/Download/${id}`);
    }
    return this.createUrl(`Poe/AssessmentGuideDocs/Download/${id}`);
  }

  public archivePoeAssessmentGuideDocs(id: number) {
    return this.createUrl(`Poe/AssessmentGuideDocs/Archive/${id}`);
  }

  public poeAssessmentGuideDocsAnnotationData(
    id: number = null,
    isChild = false
  ) {
    if (isChild) {
      if (id === null) {
        return this.createUrl('Poe/Child/AssessmentGuideDocs/AnnotationData');
      }
      return this.createUrl(
        `Poe/Child/AssessmentGuideDocs/AnnotationData/${id}`
      );
    }

    if (id === null) {
      return this.createUrl('Poe/AssessmentGuideDocs/AnnotationData');
    }
    return this.createUrl(`Poe/AssessmentGuideDocs/AnnotationData/${id}`);
  }

  public replacePoeAssessmentGuideDoc(docId: number, isChild = false) {
    if (isChild) {
      return this.createUrl(`Poe/Child/AssessmentGuideDocs/Replace/${docId}`);
    }
    return this.createUrl(`Poe/AssessmentGuideDocs/Replace/${docId}`);
  }

  public poeFeedbackDocs(id: number, isChild = false) {
    if (isChild) {
      return this.createUrl(`Poe/Child/FeedbackDocs/${id}`);
    }
    return this.createUrl(`Poe/FeedbackDocs/${id}`);
  }

  public downloadPoeFeedbackDocs(id: number, isChild = false) {
    if (isChild) {
      return this.createUrl(`Poe/Child/FeedbackDocs/Download/${id}`);
    }
    return this.createUrl(`Poe/FeedbackDocs/Download/${id}`);
  }

  public archivePoeFeedbackDocs(id: number) {
    return this.createUrl(`Poe/FeedbackDocs/Archive/${id}`);
  }

  public poeFeedbackDocsAnnotationData(id: number = null, isChild = false) {
    if (isChild) {
      if (id === null) {
        return this.createUrl('Poe/Child/FeedbackDocs/AnnotationData');
      }
      return this.createUrl(`Poe/Child/FeedbackDocs/AnnotationData/${id}`);
    }

    if (id === null) {
      return this.createUrl('Poe/FeedbackDocs/AnnotationData');
    }
    return this.createUrl(`Poe/FeedbackDocs/AnnotationData/${id}`);
  }

  public replacePoeFeedbackDoc(docId: number, isChild = false) {
    if (isChild) {
      return this.createUrl(`Poe/Child/FeedbackDocs/Replace/${docId}`);
    }
    return this.createUrl(`Poe/FeedbackDocs/Replace/${docId}`);
  }

  public poeWorkplaceDocs(id: number, isChild = false) {
    if (isChild) {
      return this.createUrl(`Poe/Child/WorkplaceDocs/${id}`);
    }
    return this.createUrl(`Poe/WorkplaceDocs/${id}`);
  }

  public downloadPoeWorkplaceDocs(id: number, isChild = false) {
    if (isChild) {
      return this.createUrl(`Poe/Child/WorkplaceDocs/Download/${id}`);
    }
    return this.createUrl(`Poe/WorkplaceDocs/Download/${id}`);
  }

  public archivePoeWorkplaceDocs(id: number) {
    return this.createUrl(`Poe/WorkplaceDocs/Archive/${id}`);
  }

  public poeWorkplaceDocsAnnotationData(id: number = null, isChild = false) {
    if (isChild) {
      if (id === null) {
        return this.createUrl('Poe/Child/WorkplaceDocs/AnnotationData');
      }
      return this.createUrl(`Poe/Child/WorkplaceDocs/AnnotationData/${id}`);
    }

    if (id === null) {
      return this.createUrl('Poe/WorkplaceDocs/AnnotationData');
    }
    return this.createUrl(`Poe/WorkplaceDocs/AnnotationData/${id}`);
  }

  public replacePoeWorkplaceDoc(docId: number, isChild = false) {
    if (isChild) {
      return this.createUrl(`Poe/Child/WorkplaceDocs/Replace/${docId}`);
    }
    return this.createUrl(`Poe/WorkplaceDocs/Replace/${docId}`);
  }

  public poeGroupFormativeAssessmentDocs(groupId: number, isChild = false) {
    if (isChild) {
      return this.createUrl(`Poe/Child/GroupAssessmentDocs/${groupId}`);
    }
    return this.createUrl(`Poe/GroupAssessmentDocs/${groupId}`);
  }

  public poeGroupSummativeAssessmentDocs(groupId: number, isChild = false) {
    if (isChild) {
      return this.createUrl(`Poe/Child/GroupAssessmentDocs/${groupId}`);
    }
    return this.createUrl(`Poe/GroupAssessmentDocs/${groupId}`);
  }

  public downloadPoeGroupSummativeAssessmentDocs(
    docId: number,
    isChild = false
  ) {
    if (isChild) {
      return this.createUrl(`Poe/Child/GroupAssessmentDocs/Download/${docId}`);
    }
    return this.createUrl(`Poe/GroupAssessmentDocs/Download/${docId}`);
  }

  public archivePoeGroupSummativeAssessmentDocs(docId: number) {
    return this.createUrl(`Poe/GroupAssessmentDocs/Archive/${docId}`);
  }

  public downloadPoeGroupFormativeAssessmentDocs(
    docId: number,
    isChild = false
  ) {
    if (isChild) {
      return this.createUrl(`Poe/Child/GroupAssessmentDocs/Download/${docId}`);
    }
    return this.createUrl(`Poe/GroupAssessmentDocs/Download/${docId}`);
  }

  public archivePoeGroupFormativeAssessmentDocs(docId: number) {
    return this.createUrl(`Poe/GroupAssessmentDocs/Archive/${docId}`);
  }

  public poeGroupFormativeAssessmentDocsAnnotationData(
    id: number = null,
    isChild = false
  ) {
    if (isChild) {
      if (id === null) {
        return this.createUrl('Poe/Child/GroupAssessmentDocs/AnnotationData');
      }
      return this.createUrl(
        `Poe/Child/GroupAssessmentDocs/AnnotationData/${id}`
      );
    }

    if (id === null) {
      return this.createUrl('Poe/GroupAssessmentDocs/AnnotationData');
    }
    return this.createUrl(`Poe/GroupAssessmentDocs/AnnotationData/${id}`);
  }

  public poeGroupSummativeAssessmentDocsAnnotationData(
    id: number = null,
    isChild = false
  ) {
    if (isChild) {
      if (id === null) {
        return this.createUrl('Poe/Child/GroupAssessmentDocs/AnnotationData');
      }
      return this.createUrl(
        `Poe/Child/GroupAssessmentDocs/AnnotationData/${id}`
      );
    }

    if (id === null) {
      return this.createUrl('Poe/GroupAssessmentDocs/AnnotationData');
    }
    return this.createUrl(`Poe/GroupAssessmentDocs/AnnotationData/${id}`);
  }

  public replacePoeGroupFormativeAssessmentDoc(docId: number, isChild = false) {
    if (isChild) {
      return this.createUrl(`Poe/Child/GroupAssessmentDocs/Replace/${docId}`);
    }
    return this.createUrl(`Poe/GroupAssessmentDocs/Replace/${docId}`);
  }

  public replacePoeGroupSummativeAssessmentDoc(docId: number, isChild = false) {
    if (isChild) {
      return this.createUrl(`Poe/Child/GroupAssessmentDocs/Replace/${docId}`);
    }
    return this.createUrl(`Poe/GroupAssessmentDocs/Replace/${docId}`);
  }

  public poeGroupGeneralDocs(groupId: number, isChild = false) {
    if (isChild) {
      return this.createUrl(`Poe/Child/GroupGeneralDocs/${groupId}`);
    }
    return this.createUrl(`Poe/GroupGeneralDocs/${groupId}`);
  }

  public downloadPoeGroupGeneralDocs(docId: number, isChild = false) {
    if (isChild) {
      return this.createUrl(`Poe/Child/GroupGeneralDocs/Download/${docId}`);
    }
    return this.createUrl(`Poe/GroupGeneralDocs/Download/${docId}`);
  }

  public archivePoeGroupGeneralDocs(docId: number) {
    return this.createUrl(`Poe/GroupGeneralDocs/Archive/${docId}`);
  }

  public poeGroupGeneralDocAnnotationData(id: number = null, isChild = false) {
    if (isChild) {
      if (id === null) {
        return this.createUrl('Poe/Child/GroupGeneralDocs/AnnotationData');
      }
      return this.createUrl(`Poe/Child/GroupGeneralDocs/AnnotationData/${id}`);
    }

    if (id === null) {
      return this.createUrl('Poe/GroupGeneralDocs/AnnotationData');
    }
    return this.createUrl(`Poe/GroupGeneralDocs/AnnotationData/${id}`);
  }

  public replacePoeGroupGeneralDoc(docId: number, isChild = false) {
    if (isChild) {
      return this.createUrl(`Poe/Child/GroupGeneralDocs/Replace/${docId}`);
    }
    return this.createUrl(`Poe/GroupGeneralDocs/Replace/${docId}`);
  }

  public poeGroupSaqaDocs(groupId: number, isChild = false) {
    if (isChild) {
      return this.createUrl(`Poe/Child/GroupSaqaDocs/${groupId}`);
    }
    return this.createUrl(`Poe/GroupSaqaDocs/${groupId}`);
  }

  public downloadPoeGroupSaqaDocs(docId: number, isChild = false) {
    if (isChild) {
      return this.createUrl(`Poe/Child/GroupSaqaDocs/Download/${docId}`);
    }
    return this.createUrl(`Poe/GroupSaqaDocs/Download/${docId}`);
  }

  public archivePoeGroupSaqaDocs(docId: number) {
    return this.createUrl(`Poe/GroupSaqaDocs/Archive/${docId}`);
  }

  public replacePoeGroupSaqaDoc(docId: number, isChild = false) {
    if (isChild) {
      return this.createUrl(`Poe/Child/GroupSaqaDocs/Replace/${docId}`);
    }
    return this.createUrl(`Poe/GroupSaqaDocs/Replace/${docId}`);
  }

  public poeGroupSaqaDocAnnotationData(id: number = null, isChild = false) {
    if (isChild) {
      if (id === null) {
        return this.createUrl('Poe/Child/GroupSaqaDocs/AnnotationData');
      }
      return this.createUrl(`Poe/Child/GroupSaqaDocs/AnnotationData/${id}`);
    }

    if (id === null) {
      return this.createUrl('Poe/GroupSaqaDocs/AnnotationData');
    }
    return this.createUrl(`Poe/GroupSaqaDocs/AnnotationData/${id}`);
  }

  public qualPOEByQualificationId(qualificationId: number): string {
    return this.createUrl(`POE/qualPOE/byQual/${qualificationId}`);
  }

  public programmePOEByProgrammeId(programmeId: number): string {
    return this.createUrl(`POE/programmePOE/byProgramme/${programmeId}`);
  }

  public studentPOEByStudentId(studentId: number): string {
    return this.createUrl(`POE/studentPOE/byStudent/${studentId}`);
  }

  public studentPOEById(studentPoeId: number): string {
    return this.createUrl(`POE/studentPOE/${studentPoeId}`);
  }

  public currentStudentPOE(): string {
    return this.createUrl('POE/studentPOE/myPOE');
  }

  public currentStudentLearnerships(): string {
    return this.createUrl('Personal/student/my-learnerships');
  }

  public currentStudentChatRoomName(): string {
    return this.createUrl('ChatHub/studentChatRoomName');
  }

  public getCurrentStaffMembersLearnershipRoles(learnershipId: number): string {
    return this.createUrl(
      `Learnership/currentStaffLearnershipRoles/${learnershipId}`
    );
  }

  public getCurrentStaffMembersLearnershipRolesByStudentLearnershipId(
    studentLearnershipId: number
  ): string {
    return this.createUrl(
      `Learnership/current-staff-learnership-roles-by-student-learnership/${studentLearnershipId}`
    );
  }

  public unlockPoeById(): string {
    return this.createUrl(`POE/unlock`);
  }

  public lockPoeById(): string {
    return this.createUrl(`POE/lock`);
  }

  public qualPOEGroupsByQualificationId(qualificationId: number): string {
    return this.createUrl(`POE/qualPOEGroups/byQual/${qualificationId}`);
  }

  public programmePOEGroupsByProgrammeId(programmeId: number): string {
    return this.createUrl(`POE/programmePOEGroups/byProgramme/${programmeId}`);
  }

  public studentPOEGroupsByStudentId(studentId: number): string {
    return this.createUrl(`POE/studentPOEGroups/byStudent/${studentId}`);
  }

  public poeDocuments(
    poeId: number,
    poeType: number,
    poeFolder: number,
    poeGroupId: number | null = null
  ) {
    return this.createUrlWithQueryParameters('POE/documents', (queryParams) => {
      queryParams.push('POEId', poeId);
      queryParams.push('POEType', poeType);
      queryParams.push('POEFolder', poeFolder);
      if (poeGroupId != null) {
        queryParams.push('POEGroupId', poeGroupId);
      }
    });
  }

  public downloadPOEDocument(
    docId: number,
    poeType: LmsApi.POETypes,
    poeFolder: LmsApi.POEFolders
  ) {
    return this.createUrlWithQueryParameters(
      `POE/document/download/${docId}`,
      (queryParams) => {
        queryParams.push('poeType', poeType);
        queryParams.push('poeFolder', poeFolder);
      }
    );
  }

  public downloadProgrammePOEScheduleDocument(docId: number) {
    return this.createUrl(`POE/programmePOE/schedule/download/${docId}`);
  }

  public savePOEDocument() {
    return this.createUrl('POE/document/save');
  }

  public savePOEDocumentInputData() {
    return this.createUrl('POE/qualPOE/inputData/save');
  }

  public poePDFViewerDocumentInfo(
    docId: number,
    poeType: LmsApi.POETypes,
    poeFolder: LmsApi.POEFolders
  ) {
    return this.createUrlWithQueryParameters(
      `POE/document/pdfViewerInfo/${docId}`,
      (queryParams) => {
        queryParams.push('poeType', poeType);
        queryParams.push('poeFolder', poeFolder);
      }
    );
  }

  public programmePOESchedulePDFViewerDocument(docId: number) {
    return this.createUrlWithQueryParameters(
      `POE/programmePOE/schedule/pdfViewer/${docId}`
    );
  }

  public programmePOESchedulePDFViewerDocumentInfo(docId: number) {
    return this.createUrlWithQueryParameters(
      `POE/programmePOE/scheduleInfo/pdfViewer/${docId}`
    );
  }

  public deletePOEDocument(
    docId: number,
    poeType: LmsApi.POETypes,
    poeFolder: LmsApi.POEFolders
  ) {
    return this.createUrlWithQueryParameters(
      `POE/document/${docId}`,
      (queryParams) => {
        queryParams.push('poeType', poeType);
        queryParams.push('poeFolder', poeFolder);
      }
    );
  }

  public archivePOEDocument(
    docId: number,
    poeType: LmsApi.POETypes,
    poeFolder: LmsApi.POEFolders
  ) {
    return this.createUrlWithQueryParameters(
      `POE/document/archive/${docId}`,
      (queryParams) => {
        queryParams.push('poeType', poeType);
        queryParams.push('poeFolder', poeFolder);
      }
    );
  }

  public poeDocumentsWithParams(
    poeId: number,
    poeType: LmsApi.POETypes,
    poeFolder: LmsApi.POEFolders,
    poeGroupId: number = null
  ): string {
    if (poeGroupId == null) {
      return this.createUrlWithQueryParameters(
        `POE/documents/${poeId}`,
        (queryParams) => {
          queryParams.push('poeType', poeType);
          queryParams.push('poeFolder', poeFolder);
        }
      );
    }
    return this.createUrlWithQueryParameters(
      `POE/documents/${poeId}/${poeGroupId}`,
      (queryParams) => {
        queryParams.push('poeType', poeType);
        queryParams.push('poeFolder', poeFolder);
      }
    );
  }

  public poeDocument(
    docId: number,
    poeType: LmsApi.POETypes,
    poeFolder: LmsApi.POEFolders
  ): string {
    return this.createUrlWithQueryParameters(
      `POE/document/${docId}`,
      (queryParams) => {
        queryParams.push('poeType', poeType);
        queryParams.push('poeFolder', poeFolder);
      }
    );
  }

  public pdfDocumentAnnotations(access: AccessLevels, docId: number): string {
    switch (access) {
      case AccessLevels.programmeLevel:
        return this.createUrlWithQueryParameters(
          `Learnership/document/annotations/${docId}`
        );
      case AccessLevels.studentLevel:
        return this.createUrlWithQueryParameters(
          `Student/document/annotations/${docId}`
        );
      default:
        break;
    }
  }

  public pdfDocumentInputs(access: AccessLevels, docId: number): string {
    switch (access) {
      case AccessLevels.qualificationLevel:
        return this.createUrlWithQueryParameters(
          `Qualification/document/inputs/${docId}`
        );
      case AccessLevels.programmeLevel:
        return this.createUrlWithQueryParameters(
          `Learnership/document/inputs/${docId}`
        );
      case AccessLevels.studentLevel:
        return this.createUrlWithQueryParameters(
          `Student/document/inputs/${docId}`
        );
      default:
        break;
    }
  }

  public poeDocumentAnnotations(
    docId: number,
    poeType: LmsApi.POETypes,
    poeFolder: LmsApi.POEFolders
  ): string {
    return this.createUrlWithQueryParameters(
      `POE/document/annotations/${docId}`,
      (queryParams) => {
        queryParams.push('poeType', poeType);
        queryParams.push('poeFolder', poeFolder);
      }
    );
  }

  public poeDocumentInputs(
    docId: number,
    poeType: LmsApi.POETypes,
    poeFolder: LmsApi.POEFolders
  ): string {
    return this.createUrlWithQueryParameters(
      `POE/document/inputs/${docId}`,
      (queryParams) => {
        queryParams.push('poeType', poeType);
        queryParams.push('poeFolder', poeFolder);
      }
    );
  }

  public allDocumentTypes() {
    return this.createUrl('DocType/all');
  }

  public documentTypes(type: LmsApi.DocTypeCatagories = null) {
    if (this.isNull(type)) {
      return this.createUrl('DocType');
    }
    return this.createUrlWithQueryParameters('DocType', (queryString) => {
      queryString.push('documentTypeCatagory', type);
    });
  }

  public poe(id?: number): string {
    if (!id) {
      return this.createUrl('Poe');
    } else {
      return this.createUrl(`Poe/${id}`);
    }
  }

  childPoe(id?: number): string {
    if (!id) {
      return this.createUrl('Poe/Child');
    } else {
      return this.createUrl(`Poe/Child/${id}`);
    }
  }

  public childPoeByStudent(id: number): string {
    return this.createUrl(`Poe/ByStudent/${id}`);
  }

  public fixPOE(): string {
    return this.createUrl('Poe/fix');
  }

  public employer(id: number = null): string {
    if (this.isNull(id)) {
      return this.createUrl('Employer');
    }
    return this.createUrl(`Employer/${id}`);
  }

  public archiveEmployer(id: number): string {
    return this.createUrl(`Employer/archive/${id}`);
  }

  public getConfirmRegInfo(): string {
    return this.createUrl('Tenant/confirm-registration-info');
  }

  public getResetPasswordInfo(): string {
    return this.createUrl('Tenant/reset-password-info');
  }
  // URL
  private createUrl(action: string, base = false): string {
    let url = this.configService.config?.lmsApiUrl;
    if (base) {
      url = url.split('/api')[0];
    }
    const urlBuilder: UrlBuilder = new UrlBuilder(url, action);
    return urlBuilder.toString();
  }

  // URL WITH QUERY PARAMS
  private createUrlWithQueryParameters(
    action: string,
    queryStringHandler?: (queryStringParameters: QueryStringParameters) => void
  ): string {
    const urlBuilder: UrlBuilder = new UrlBuilder(
      this.configService.config?.lmsApiUrl,
      action
    );
    // Push extra query string params
    if (queryStringHandler) {
      queryStringHandler(urlBuilder.queryString);
    }
    return urlBuilder.toString();
  }

  // URL WITH PATH VARIABLES
  private createUrlWithPathVariables(
    action: string,
    pathVariables: any[] = []
  ): string {
    let encodedPathVariablesUrl = '';
    // Push extra path variables
    for (const pathVariable of pathVariables) {
      if (pathVariable !== null) {
        encodedPathVariablesUrl += `/${encodeURIComponent(
          pathVariable.toString()
        )}`;
      }
    }
    const urlBuilder: UrlBuilder = new UrlBuilder(
      this.configService.config?.lmsApiUrl,
      `${action}${encodedPathVariablesUrl}`
    );
    return urlBuilder.toString();
  }

  private isNull(value: any) {
    if (value === null) {
      return true;
    }
    return false;
  }
}
